import { forwardRef, LegacyRef, ReactNode, useMemo, useState } from 'react';
import ClearIcon from '../../assets/clear.svg';
import VisibilityOffIcon from '../../assets/visibility-off.svg';
import VisibilityIcon from '../../assets/visibility.svg';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string | ReactNode;
  name: string;
  required?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
  errorList?: Array<string | ReactNode>;
  warningList?: Array<string | ReactNode>;
  disabled?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  ref?: LegacyRef<HTMLInputElement> | undefined;
  viewMode?: ViewMode;
}

export enum ViewMode {
  View = 'View',
  Edit = 'Edit',
  Text = 'Text',
}

// @ts-ignore -- typescript rollup plugin is throwing a warning here, because ...args is not resolved, because tslib is not found, but it compiles, so we can ignore it
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      required,
      name,
      hasError,
      errorList,
      warningList,
      hasWarning,
      disabled,
      clearable,
      onClear,
      viewMode = ViewMode.Edit,
      ...args
    }: InputProps,
    ref,
  ) => {
    const cssClasses = useMemo(() => {
      const classes = [];
      if (required) {
        classes.push('form-input--required');
      }
      if (disabled) {
        classes.push('form-input--disabled');
      }
      if (hasError || (errorList && errorList.length > 0)) {
        classes.push('form-input--error');
      }
      if (hasWarning || (warningList && warningList.length > 0)) {
        classes.push('form-input--warning');
      }

      if (clearable) {
        classes.push('form-input--clearable');
      }
      return classes.join(' ');
    }, [required, disabled, hasError, errorList]);

    const [showPassword, setShowPassword] = useState(false);

    // Accessibility: error/warning IDs
    const errorId = errorList && errorList.length > 0 ? `${name}-error-list` : undefined;
    const warningId = warningList && warningList.length > 0 ? `${name}-warning-list` : undefined;
    const describedBy = [errorId, warningId].filter(Boolean).join(' ') || undefined;

    return (
      <>
        <div className={`form-input ${cssClasses}${args.inputMode === 'tel' ? ' form-input--tel' : ''}`}>
          <div className={'form-input__input-container'}>
            <div className={'form-input__input-inner-container'}>
              <label htmlFor={name}>{label}</label>
              {args.inputMode === 'tel' ? (
                <div className={'form-input__tel-wrapper'}>
                  <span className={'form-input__tel-prefix'}>+36</span>
                  <input
                    id={name}
                    name={name}
                    disabled={disabled}
                    ref={ref}
                    aria-label={typeof label === 'string' ? label : 'Beviteli mező'}
                    aria-describedby={describedBy}
                    aria-required={required ? 'true' : undefined}
                    placeholder="20 345 6789"
                    {...args}
                  />
                </div>
              ) : (
                <>
                  {viewMode === ViewMode.Text ? (
                    <span className={'form-input__text-mode'}>{args.value}</span>
                  ) : (
                    <input
                      id={name}
                      name={name}
                      disabled={disabled}
                      ref={ref}
                      aria-label={typeof label === 'string' ? label : 'Beviteli mező'}
                      aria-describedby={describedBy}
                      aria-required={required ? 'true' : undefined}
                      {...args}
                      type={args.type === 'password' ? (showPassword ? 'text' : 'password') : args.type}
                    />
                  )}
                </>
              )}
              {args.type === 'password' && (
                <span
                  className={'form-input__toggle-password'}
                  role="button"
                  tabIndex={0}
                  aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó mutatása'}
                  onClick={() => setShowPassword(prev => !prev)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === 'NumpadEnter' || e.key === ' ') {
                      setShowPassword(prev => !prev);
                    }
                  }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </span>
              )}
            </div>
          </div>
          {clearable && (
            <span
              className={'form-input__clear'}
              role="button"
              tabIndex={0}
              aria-label="Tartalom törlése"
              onClick={() => onClear && onClear()}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'NumpadEnter' || e.key === ' ') {
                  onClear && onClear();
                }
              }}
            >
              <ClearIcon />
            </span>
          )}
          {errorList && errorList.length > 0 && (
            <ul
              className={'form-input__error-list'}
              id={errorId}
              aria-live="assertive"
            >
              {errorList.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          )}
          {warningList && warningList.length > 0 && (
            <ul
              className={'form-input__warning-list'}
              id={warningId}
              aria-live="polite"
            >
              {warningList.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      </>
    );
  },
);
